import React, { useMemo, forwardRef, useState, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import Grid from '@material-ui/core/Grid'
import MenuBookIcon from '@material-ui/icons/MenuBook'
import { useRouter } from 'next/router'

import TypographyPro from 'themes/TypographyPro'
import ImageComponent from 'themes/ImageComponent'
import { useStores } from 'hooks/useStores'
import { useCurrentUrlInfo } from 'hooks/useCurrentUrlInfo'

import type _User from 'mobx/User'
import type _Account from 'mobx/Account'
import type _Infra from 'mobx/Infra'

import { getTranslatedTextByKey, routeToPage } from 'utils/utils'
import { Page, EXTERNAL_ADDRESSBOX_ID } from 'utils/constants'

import LocalizedContent from './LocalizedContent'

import {
	ToMenuButtonContainer,
	ToMenuButton,
	Container,
	StyledTextGrid,
	GridContainer,
	TextContainer,
	OrderButton,
	OrderButtonIcon,
	LocalizedContainer,
	LocalizedLeftContainer,
	TypographyProFullWidth,
	ClickHereText
} from './style'
import { AddressFailedToSaveEvent, AddressSavedEvent, EventKey, OrderTypeFlowsUnavailableEvent } from 'mobx/Events/types'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'

const LocalizationHeader = inject(
	'User',
	'Account',
	'Infra'
)(
	observer(
		forwardRef(({ User, Account, Infra }: { User: typeof _User; Account: typeof _Account; Infra: typeof _Infra }, ref) => {
			const { localizationStore, eventStore } = useStores()
			const { pathname } = useCurrentUrlInfo()
			const [alreadyLocalized, setAlreadyLocalized] = useState(false)
			const userLanguage = User.preferredLanguage ?? 'en'
			const router = useRouter()

			useEffect(() => {
				if (eventStore.hasHandler('addressSavedSnackbar')) {
					return
				}

				eventStore.registerHandler<AddressSavedEvent>('addressSavedSnackbar', EventKey.AddressSaved, () => {
					Infra.showSnackbar({
						snackId: 'addressSavedSuccessfully',
						message: getTranslatedTextByKey('webviewFlow.localization.addressSavedSuccessfully', 'Address saved successfully'),
						status: SnackbarStatus.SUCCESS,
					})
				})

				eventStore.registerHandler<AddressFailedToSaveEvent>('addressFailedToSaveSnackbar', EventKey.AddressFailedToSave, ({ error }) => {
					console.error('Could not save address', error)
		      		Infra.setLoading(false)
					Infra.showSnackbar({
						snackId: 'addressNotSaved',
						message: getTranslatedTextByKey(
							'webviewFlow.localization.addressNotSaved',
							'Address could not be saved. Please try again later'
						),
						status: SnackbarStatus.ERROR,
					})
				})

				eventStore.registerHandler<OrderTypeFlowsUnavailableEvent>('orderTypeFlowsUnavailableNotifcation', EventKey.OrderTypeFlowsUnavailable, () => {
					allStoresAreClosedAlert()
				})
			}, [])

			useEffect(() => {
				setAlreadyLocalized(localizationStore.alreadyLocalized)
			}, [localizationStore.alreadyLocalized])

			useEffect(() => {
				const isAuthenticated = Account.isAuthenticated()

				if (isAuthenticated && !localizationStore.savedAddressesFetched) {
					localizationStore.fetchSavedAddresses()
				} else if (!isAuthenticated) {
					localizationStore.clearSavedAddresses()
				}
			}, [Account.userName])

			const allStoresAreClosedAlert = () => {
				const title = getTranslatedTextByKey('webviewFlow.storesAreClosed', 'sorry all our stores are currently closed')
				const preMessage = getTranslatedTextByKey('webviewFlow.localization.bar.allStoresClosed.message', 'All stores are closed right now. For opening hours')
				const message = <>{preMessage} <ClickHereText onClick={storeLocatorRedirection}>{getTranslatedTextByKey('webviewFlow.localization.bar.allStoresClosed.clickHereLink', 'Click here')}</ClickHereText></>

				Infra.setNotification({
					open: true,
					title,
					message: message,
					topImg: <ImageComponent asset="/images/pastOrdersEmpty.png" />,
					customAction: () => {
						Infra.closeNotification()
					},
					customButtonType: 'secondary',
					cancelText: getTranslatedTextByKey('menu.backToMenu', 'Back to menu'),
					showCancelButton: false,
					cancelAction: () => {
						Infra.closeNotification()
					},
				})
			}

			const storeLocatorRedirection = (e: React.MouseEvent<HTMLElement>) => {
				e.stopPropagation()
				Infra.closeNotification()
		
				router.push(`/${userLanguage}/store-locator`)
			}

			const translationKeys = useMemo(
				() => ({
					title: getTranslatedTextByKey('webFlowHomePageHeader'),
					subTitle: getTranslatedTextByKey('webFlowHomePageSubHeader'),
				}),
				[User.preferredLanguage]
			)

			const textExists = translationKeys?.title || translationKeys?.subTitle
			const page = routeToPage(pathname)
			const displayToMenuButton = localizationStore.config().displayToMenuButton && page !== Page.MENU

			return (
				<Container data-testid="order-type-and-localization-container" ref={ref} id={EXTERNAL_ADDRESSBOX_ID}>
					{!alreadyLocalized && (
						<GridContainer container justifyContent="center" alignItems="center">
							{textExists && (
								<StyledTextGrid item xs="auto" sm={6} $alreadyLocalized={localizationStore.alreadyLocalized}>
									<TextContainer>
										{translationKeys?.title && (
											<TypographyProFullWidth variant="HomePageTitle" component="h1" data-testid="address-box-title">
												{translationKeys?.title}
											</TypographyProFullWidth>
										)}
										{translationKeys?.subTitle && (
											<TypographyProFullWidth variant="BodySmall" component="h2" data-testid="address-box-subtitle">
												{translationKeys?.subTitle}
											</TypographyProFullWidth>
										)}
									</TextContainer>
								</StyledTextGrid>
							)}
							<Grid item xs sm={6}>
								<OrderButton data-testid="order-now-button-new" onClick={localizationStore.start}>
									<TypographyPro variant="h2">{getTranslatedTextByKey('webviewFlow.startOrder', 'Order now')}</TypographyPro>
									<OrderButtonIcon asset="/icons/orderNow.svg" />
								</OrderButton>
							</Grid>
						</GridContainer>
					)}

					{alreadyLocalized && (
						<LocalizedContainer data-testid="localized-container">
							<LocalizedLeftContainer>
								<LocalizedContent />
							</LocalizedLeftContainer>
							{displayToMenuButton && (
								<ToMenuButtonContainer>
									<ToMenuButton data-testid="to-menu-button" onClick={localizationStore.goToLocalizedMenu}>
										<MenuBookIcon />
										<span>{getTranslatedTextByKey('webviewFlow.localization.toMenu', 'To menu')}</span>
									</ToMenuButton>
								</ToMenuButtonContainer>
							)}
						</LocalizedContainer>
					)}
				</Container>
			)
		})
	)
)

export default LocalizationHeader
